import { APP_BUILD_INFO } from '../buildInfo'
import { translate } from '../i18n'
import { setCurrentFastSyncError } from '../state/currentFastStore'
import {
   sanitizeLastPersistMeta,
   type SyncDebugInfo,
   type SyncPersistMeta,
} from '../storage/localPersistence'
import {
   deserializeFastEntry,
   ensureCurrentFastDurationUpdatedAt,
   type FastDisplayMode,
   type FastEntry,
} from '../utils/fastEntry'
import { parseTimestampToNumber } from '../utils/time'
import { createSyncFailureTracker, type SyncFailureState } from './syncFailureTracker'

export type { SyncFailureState }

export type SyncStateOptions = {
   keepalive?: boolean
   retryAfterConflict?: boolean
   clearStaleMetadataAfterConflict?: boolean
}

export type SyncStateResult = Record<string, unknown> | boolean

type SyncRequestSnapshot = {
   currentFast: Record<string, unknown> | null
   weightChartMode: unknown
}

type SyncEngineDeps = {
   syncEndpoint: string
   syncRetryIntervalMs: number
   maxKeepaliveBodyBytes: number
   buildRequestSnapshot: () => SyncRequestSnapshot
   canUseRemoteSync: () => boolean
   getUserId: () => string
   resolveSyncUserId: () => Promise<string>
   getStateVersion: () => number
   getDisplayModePreference: () => FastDisplayMode
   getUserProfile: () => {
      heightCm: number | null
      targetWeightKg: number | null
      targetMonthlyRunEffort: number | null
      preferredLocale: string | null
      unitSystem: 'metric' | 'imperial'
   }
   getSyncDebugInfo: () => SyncDebugInfo
   persistDebugInfo: () => void
   loadRemoteState: (options?: Record<string, unknown>) => Promise<unknown>
   restoreCurrentFast: (entry: FastEntry) => void
   syncAccountStateToSolid: () => void
}

type SyncRequestContext = {
   resolvedAction: string
   snapshot: SyncRequestSnapshot
   requestStateVersion: number
   clientTimestamp: number
   syncOptions: SyncStateOptions
}

type SyncEngineContext = {
   deps: SyncEngineDeps
   failures: ReturnType<typeof createSyncFailureTracker>
   syncState: (action: string, syncOptions?: SyncStateOptions) => Promise<SyncStateResult>
}

/**
 * Builds the JSON body for a sync request from the snapshot and user profile.
 *
 * @param deps - The engine dependencies.
 * @param context - The sync request context.
 * @param hashedId - The hashed user id.
 * @returns The serialized request body.
 */
function buildSyncRequestBody(
   deps: SyncEngineDeps,
   context: SyncRequestContext,
   hashedId: string,
) {
   const userProfile = deps.getUserProfile()
   return JSON.stringify({
      userId: hashedId,
      action: context.resolvedAction,
      currentFast: context.snapshot.currentFast,
      clientBuildInfo: APP_BUILD_INFO,
      heightCm: typeof userProfile.heightCm === 'number' ? userProfile.heightCm : null,
      targetWeightKg:
         typeof userProfile.targetWeightKg === 'number' ?
            userProfile.targetWeightKg
         :  null,
      targetMonthlyRunEffort:
         typeof userProfile.targetMonthlyRunEffort === 'number' ?
            userProfile.targetMonthlyRunEffort
         :  null,
      preferredLocale:
         typeof userProfile.preferredLocale === 'string' ?
            userProfile.preferredLocale
         :  null,
      unitSystem: userProfile.unitSystem,
      weightChartMode: context.snapshot.weightChartMode,
      displayModePreference: deps.getDisplayModePreference(),
      clientTimestamp: context.clientTimestamp,
      clientStateVersion: context.requestStateVersion,
      lastPersistMeta:
         (
            deps.getSyncDebugInfo().lastPersist &&
            typeof deps.getSyncDebugInfo().lastPersist === 'object'
         ) ?
            { ...deps.getSyncDebugInfo().lastPersist }
         :  null,
   })
}

/**
 * Parses a sync response body as JSON, returning null for non-JSON or parse errors.
 *
 * @param response - The fetch response.
 * @returns The parsed object, or null.
 */
async function parseJsonResponseBody(
   response: Response,
): Promise<Record<string, unknown> | null> {
   const contentType = response.headers.get('content-type') || ''
   if (!contentType.includes('application/json')) {
      return null
   }
   try {
      const rawResponseBody: unknown = await response.json()
      return rawResponseBody && typeof rawResponseBody === 'object' ?
            (rawResponseBody as Record<string, unknown>)
         :  null
   } catch (error) {
      console.error('Failed to parse sync response JSON', error)
      return null
   }
}

/**
 * Restores the in-flight current fast into the store after a conflict refresh.
 *
 * @param deps - The engine dependencies.
 * @param context - The sync request context.
 * @returns True when a pending fast was restored.
 */
function restorePendingCurrentFast(deps: SyncEngineDeps, context: SyncRequestContext) {
   const pendingCurrentFast = deserializeFastEntry(context.snapshot.currentFast)
   if (!pendingCurrentFast) {
      return false
   }
   ensureCurrentFastDurationUpdatedAt(pendingCurrentFast, Date.now())
   deps.restoreCurrentFast(pendingCurrentFast)
   return true
}

/**
 * Retries a start-fast that lost a sync conflict, refreshing remote state and optionally clearing stale metadata.
 *
 * @param engine - The engine context.
 * @param context - The sync request context.
 * @returns The retried sync result, or false when it could not be completed.
 */
async function retryStartFastAfterConflict(
   engine: SyncEngineContext,
   context: SyncRequestContext,
): Promise<SyncStateResult> {
   try {
      await engine.deps.loadRemoteState({ skipTabRefresh: true })
      restorePendingCurrentFast(engine.deps, context)
      const retryResult: SyncStateResult = await engine.syncState(
         context.resolvedAction,
         {
            ...context.syncOptions,
            retryAfterConflict: false,
         },
      )
      if (retryResult !== false) {
         setCurrentFastSyncError('')
         return retryResult
      }
      if (context.syncOptions.clearStaleMetadataAfterConflict !== false) {
         engine.failures.clearStaleSyncMetadata()
         await engine.deps.loadRemoteState({ skipTabRefresh: true })
         restorePendingCurrentFast(engine.deps, context)
         const resetRetryResult: SyncStateResult = await engine.syncState(
            context.resolvedAction,
            {
               ...context.syncOptions,
               clearStaleMetadataAfterConflict: false,
               retryAfterConflict: false,
            },
         )
         if (resetRetryResult !== false) {
            setCurrentFastSyncError('')
            return resetRetryResult
         }
      }
   } catch (error) {
      console.error('Failed to retry fast start after conflict', error)
   }
   setCurrentFastSyncError(
      translate(
         'Could not start fast because cloud sync is still catching up. Please try again.',
      ),
   )
   return false
}

/**
 * Handles a 409 sync conflict: retries start-fast or reloads remote state and surfaces an error.
 *
 * Other actions are re-pushed once after the refresh. The local change (e.g.
 * a one-off task's deletion tombstone) would otherwise sit unsynced until the
 * next user action, and meanwhile other devices keep echoing the stale habit.
 *
 * @param engine - The engine context.
 * @param context - The sync request context.
 * @param responseBody - The parsed conflict response.
 * @returns The follow-up sync result, or false.
 */
async function handleSyncConflict(
   engine: SyncEngineContext,
   context: SyncRequestContext,
   responseBody: Record<string, unknown>,
): Promise<SyncStateResult> {
   const isStartFastConflict =
      context.resolvedAction === 'start-fast' && Boolean(context.snapshot.currentFast)
   const conflictingCurrentFast = deserializeFastEntry(responseBody.currentFast)
   console.warn('Sync conflict detected; refreshing state from server.')
   if (
      isStartFastConflict &&
      !conflictingCurrentFast &&
      context.syncOptions.retryAfterConflict !== false
   ) {
      return retryStartFastAfterConflict(engine, context)
   }
   try {
      await engine.deps.loadRemoteState()
   } catch (error) {
      console.error('Failed to refresh state after conflict', error)
   }
   if (!isStartFastConflict && context.syncOptions.retryAfterConflict !== false) {
      return engine.syncState(context.resolvedAction, {
         ...context.syncOptions,
         retryAfterConflict: false,
      })
   }
   if (isStartFastConflict) {
      setCurrentFastSyncError(
         conflictingCurrentFast ?
            translate(
               'A fast is already active in cloud sync. Loaded the latest fast from your account.',
            )
         :  translate(
               'Could not start fast because cloud data changed. Review the latest synced state and try again.',
            ),
      )
   }
   engine.failures.handleSyncSuccess()
   return false
}

/**
 * Updates stored last-persist metadata from the server response, or a client fallback.
 *
 * @param deps - The engine dependencies.
 * @param context - The sync request context.
 * @param responseBody - The parsed success response.
 */
function updateLastPersistMeta(
   deps: SyncEngineDeps,

   context: SyncRequestContext,
   responseBody: Record<string, unknown>,
) {
   const syncDebugInfo = deps.getSyncDebugInfo()
   const serverMeta = sanitizeLastPersistMeta(responseBody.lastPersistMeta)
   if (serverMeta) {
      syncDebugInfo.lastPersist = serverMeta
      return
   }
   const fallbackPayload: SyncPersistMeta = {
      action: context.resolvedAction,
      clientTimestamp: context.clientTimestamp,
      clientStateVersion: context.requestStateVersion,
   }
   const fallbackServerUpdatedAt = parseTimestampToNumber(responseBody.updatedAt)
   if (fallbackServerUpdatedAt !== null) {
      fallbackPayload.serverUpdatedAt = fallbackServerUpdatedAt
   }
   const fallbackMeta = sanitizeLastPersistMeta(fallbackPayload)
   if (fallbackMeta) {
      syncDebugInfo.lastPersist = fallbackMeta
   }
}

/**
 * Returns whether an action starts or ends a fast.
 *
 * @param action - The action name.
 * @returns True for `start-fast` or `end-fast`.
 */
function isFastLifecycleAction(action: string) {
   return action === 'start-fast' || action === 'end-fast'
}

/**
 * Applies a successful sync response: metadata and banners.
 *
 * @param engine - The engine context.
 * @param context - The sync request context.
 * @param responseBody - The parsed success response.
 * @returns The response body (or true).
 */
async function applySyncSuccess(
   engine: SyncEngineContext,
   context: SyncRequestContext,
   responseBody: Record<string, unknown>,
): Promise<SyncStateResult> {
   const syncDebugInfo = engine.deps.getSyncDebugInfo()
   if (responseBody.buildInfo && typeof responseBody.buildInfo === 'object') {
      syncDebugInfo.serverBuildInfo = responseBody.buildInfo as Record<string, unknown>
   }
   updateLastPersistMeta(engine.deps, context, responseBody)
   engine.deps.persistDebugInfo()
   engine.failures.handleSyncSuccess()
   if (isFastLifecycleAction(context.resolvedAction)) {
      setCurrentFastSyncError('')
   }
   engine.deps.syncAccountStateToSolid()
   return responseBody || true
}

/**
 * Routes a sync HTTP response to conflict, failure, or success handling.
 *
 * @param engine - The engine context.
 * @param context - The sync request context.
 * @param response - The fetch response.
 * @returns The resulting sync result.
 */
async function processSyncResponse(
   engine: SyncEngineContext,
   context: SyncRequestContext,
   response: Response,
): Promise<SyncStateResult> {
   const responseBody = await parseJsonResponseBody(response)

   if (!response.ok) {
      if (response.status === 409 && responseBody && responseBody.conflict === true) {
         return handleSyncConflict(engine, context, responseBody)
      }
      engine.failures.handleSyncFailure(context.resolvedAction)
      console.error(
         'Failed to sync fasting state',
         response.status,
         response.statusText,
         responseBody,
      )
      return false
   }

   return applySyncSuccess(engine, context, responseBody ?? { ok: true })
}

/**
 * Pushes the current local state to the server for an action, handling retries and conflicts.
 *
 * @param engine - The engine context.
 * @param action - The action driving the sync.
 * @param syncOptions - Options (keepalive, conflict-retry behavior).
 * @returns The server response body, true, or false on failure/skip.
 */
async function syncState(
   engine: SyncEngineContext,

   action: string,
   syncOptions: SyncStateOptions = {},
): Promise<SyncStateResult> {
   if (!engine.deps.getUserId() || !engine.deps.canUseRemoteSync()) {
      engine.failures.clearSyncFailureState()
      return false
   }

   const context: SyncRequestContext = {
      resolvedAction: typeof action === 'string' && action ? action : 'sync',
      snapshot: engine.deps.buildRequestSnapshot(),
      requestStateVersion: engine.deps.getStateVersion(),
      clientTimestamp: Date.now(),
      syncOptions,
   }

   const hashedId = (await engine.deps.resolveSyncUserId()).trim()
   if (!hashedId) {
      engine.failures.handleSyncFailure(context.resolvedAction)
      console.error('Sync request skipped because no user ID was available.')
      return false
   }

   const requestBody = buildSyncRequestBody(engine.deps, context, hashedId)
   const requestBodySize =
      typeof TextEncoder === 'function' ?
         new TextEncoder().encode(requestBody).byteLength
      :  requestBody.length
   const useKeepalive =
      syncOptions.keepalive === true &&
      requestBodySize > 0 &&
      requestBodySize <= engine.deps.maxKeepaliveBodyBytes

   try {
      const response = await fetch(engine.deps.syncEndpoint, {
         method: 'PUT',
         keepalive: useKeepalive,
         headers: {
            'Content-Type': 'application/json',
         },
         body: requestBody,
      })
      return await processSyncResponse(engine, context, response)
   } catch (error) {
      engine.failures.handleSyncFailure(context.resolvedAction)
      console.error('Sync request failed', error)
      return false
   }
}

/**
 * Creates the cloud-sync engine that pushes local state to the server, with retry, conflict handling, and failure banners.
 *
 * @param deps - Injected dependencies (endpoint, snapshot builder, state accessors, callbacks).
 * @returns The sync engine API (`syncState`, retry, and failure-state helpers).
 */
export function createSyncEngine(deps: SyncEngineDeps) {
   const engine: SyncEngineContext = {
      deps,
      failures: createSyncFailureTracker(deps, action => {
         engine.syncState(action).catch(error => {
            console.error('Scheduled sync retry failed', error)
         })
      }),
      syncState: (action, syncOptions) => syncState(engine, action, syncOptions),
   }
   return {
      syncState: engine.syncState,
      clearSyncFailureState: engine.failures.clearSyncFailureState,
      triggerImmediateSyncRetry: engine.failures.triggerImmediateSyncRetry,
      getFailureState: engine.failures.getFailureState,
   }
}
